<template>
  <div class="lean-recursive-publish lean-recursive-publish__container">
    <v-btn v-if="checkForLinkedDocuments" @click="startDialog" outlined>
      {{ $t('publisher.recursive.button') }}
      <v-progress-circular v-if="waiting" indeterminate color="blue-grey darken-1"></v-progress-circular>
    </v-btn>
    <v-dialog v-model="showProgressDialog" persistent width="300">
      <v-card>
        <v-toolbar color="blue-grey darken-1" dark flat>
          <v-toolbar-title>{{ $t('publisher.headline') }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pa-5">
          <div v-if="!confirm" class="pb-5">
            <div class="text-center">
              {{ $t('publisher.recursive.confirm') }}
            </div>
            <div class="d-flex justify-space-between pt-4">
              <v-btn @click="startRecursivePublish">{{ $t('publisher.ok') }}</v-btn>
              <v-btn @click="showProgressDialog = false">{{ $t('publisher.cancel') }}</v-btn>
            </div>
          </div>
          <div v-else class="pb-5">
            <div v-for="(path, index) of toPublishPaths" :key="index" class="mb-2">
              <div>
                {{ $store.state.sdk.getDatatypeFromPath(path.path) }}: {{ $store.state.sdk.getSlugFromPath(path.path) }}
              </div>
              <v-progress-linear
                stream
                background-color="blue-grey lighten-3"
                color="blue-grey"
                :value="toBeCheckedPaths.length === 0 || toBeCheckedPaths.includes(path.path) ? progress : 100"
                buffer-value="0"
                height="10"
                class="mt-2"
              />
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import publishMixin from '@/mixins/LeanPublish';

export default {
  name: 'LeanRecursivePublish',
  mixins: [publishMixin],
  data: function () {
    return {
      active: true,
      confirm: false,
      linkedFields: []
    };
  },
  props: {
    existingSlug: {
      type: String,
      required: true
    },
    datatype: {
      type: String,
      required: true
    },
    resolvedData: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapState({
      models: 'models',
      language: 'selectedLanguage',
      awsExports: 'awsExports',
      sdk: 'sdk'
    }),
    checkForLinkedDocuments() {
      return !!this.$store.state.models[this.datatype]?.schema.find(
        (schemaProp) => ['document', 'document[]'].includes(schemaProp.type) && schemaProp.linked
      );
    }
  },
  methods: {
    ...mapMutations({
      setSnackbar: 'setSnackbar',
      clearSnackbar: 'clearSnackbar'
    }),
    startDialog() {
      this.confirm = false;
      this.showProgressDialog = true;
    },
    async startRecursivePublish() {
      this.confirm = true;
      const linkedDocPaths = this.getRelatedDocumentPaths(this.resolvedData, this.datatype);
      const toPublishedPaths = [
        this.sdk.getDataRootPath(this.datatype, this.existingSlug, this.language),
        ...linkedDocPaths
      ];
      this.publishByPaths(toPublishedPaths);
    },
    getRelatedDocumentPaths(resolvedData, datatype) {
      const datatypeConfig = this.$store.state.models[datatype];
      let embeddedFields = datatypeConfig.schema.filter((schemaProp) => {
        return (
          ['document', 'document[]'].includes(schemaProp.type) &&
          !schemaProp.linked &&
          schemaProp.source &&
          resolvedData[schemaProp.name]
        );
      });
      // TODO: this will work, but the resolved data does not yet include linked documents in embedded documents!
      // it's safe though, the `getLinkedDocumentsPaths()` function will simply not be able to find the references
      // recursion, get any embedded paths
      let pathList = embeddedFields.map((schemaProp) => {
        return this.getRelatedDocumentPaths(resolvedData[schemaProp.name], schemaProp.source);
      });
      // ... and then get the linked paths
      return [...pathList, ...this.getLinkedDocumentsPaths(resolvedData, datatype)];
    },
    getLinkedDocumentsPaths(resolvedData, datatype) {
      const datatypeConfig = this.$store.state.models[datatype];
      // loop over every prop from schema
      return datatypeConfig.schema.reduce((list, schemaProp) => {
        // check is current prop a document([]) and is linked and can find this in resolvedData
        if (
          ['document', 'document[]'].includes(schemaProp.type) &&
          schemaProp.linked &&
          resolvedData[schemaProp.name]
        ) {
          const linkedDocuments = resolvedData[schemaProp.name].length
            ? resolvedData[schemaProp.name]
            : [resolvedData[schemaProp.name]];
          // loop over every linked Document
          linkedDocuments.forEach((document) => {
            // ignores Files with "Reference document not found"
            if (document?._leancms) {
              // save current path to the linkedDocument
              list = [...list, this.sdk.getDataRootPath(schemaProp.source, document._leancms.slug, this.language)];
              // search for more linked Documents inside the linked Document
              list = [...list, ...this.getLinkedDocumentsPaths(document, schemaProp.source)];
            }
          });
        }
        return list;
      }, []);
    }
  }
};
</script>

<style scoped></style>
